import { _init_virtual_device_actions } from 'src/components/Scenes/helpers/virtual_components/virtual_device_actions';
import {
	get_mode,
	isPill,
	isCctOnlyDevice,
	real_id,
} from '../../../../../../helpers/interface_helpers';
import { _init_device_actions_v2 } from '../../../../helpers/init_device_actions';
import {
	format_gen2_addons_for_scenes,
	getAddonStatus,
	hasAddonPeripherals,
} from 'src/helpers/addons/addon_helpers';
import { isXT1Module } from 'src/helpers/xt1_helpers/template_helpers';
import { init_xt1_device_actions } from 'src/components/Scenes/helpers/virtual_components/xt1_device_scenes_helpers';
import { is_device_action_ui } from 'src/types/DeviceActionUI';
import type { Device } from 'src/types/Device';
import {
	readDeviceStatus,
	readDeviceSettings,
} from 'src/stores/device-runtime/status-selectors';

/**
 * Initialize action components for a device
 * @param {Object} device - The device object
 * @param {Object} status - The device status
 * @param {Object} settings - The device settings
 * @returns {Array} Array of action components
 */
export const _init_action_components_v2 = (device, status, settings) => {
	const { id, category, type } = device;
	let gen = device.gen == 2 ? 2 : 1;
	let mode,
		addOns = {};
	if (isXT1Module(device?.type)) {
		return init_xt1_device_actions(device);
	}
	if (status && settings) {
		mode = get_mode(device, status, settings);
	}

	if (category === 'emeter' && hasAddonPeripherals(device)) {
		addOns = format_gen2_addons_for_scenes(getAddonStatus(device));
	}

	let arr = [];

	/**
	 * Handle module devices that are CCT-only devices (like S3MX) as lights.
	 * These devices are always in CCT mode, so treat them as lights.
	 * @type {boolean}
	 */
	const isModuleCctLight = category === 'module' && isCctOnlyDevice(type);

	/**
	 * For CCT-only devices, default to 'cct' mode if mode isn't available
	 */
	if (isModuleCctLight && !mode) {
		mode = 'cct';
	}

	switch (category) {
		case 'pv':
			return _init_device_actions_v2[category]();
		case 'module':
			/**
			 * If module is a CCT-only device, treat it as a light
			 */
			if (isModuleCctLight) {
				return _init_device_actions_v2['light'](
					type,
					mode || 'cct',
					device.gen,
					device?.mode || 'cct',
				);
			}
		case 'lock':
			return _init_device_actions_v2[category]();
		case 'relay':
			return _init_device_actions_v2[category](gen, type);
		case 'emeter':
			return _init_device_actions_v2[category](gen, type, addOns);
		case 'roller':
			return _init_device_actions_v2[category](gen, type, device, settings);
		case 'light':
			return _init_device_actions_v2[category](type, mode, device.gen, device?.mode);
		case 'thermostat':
			return _init_device_actions_v2[category](type, settings, device);
		case 'breaker':
			return _init_device_actions_v2[category](gen, type);
		case 'virtual_device':
			return _init_virtual_device_actions(device?.channel, status, settings);
		case 'diffuser':
			/**Just a hint for scenes
			 * ciX => cury inensity left/right
			 * clX => cury level left/righ
			 * cae => cury ambient enable
			 * cab => cury ambient brightnes
			 * cac_X => cury ambient color red/green/blu
			 * ca => cury ambient to allow  {"r":"dev:...:ca", "set":{"_isdata":true,"cae":true,"cab":22,"cac_red":11,"cac_green":22,"cac_blue":33}}
			 */
			return _init_device_actions_v2[category](type);
		case 'media_player':
			return _init_device_actions_v2[category]();
		case 'camera':
			return _init_device_actions_v2[category]();
		case 'fan':
			return _init_device_actions_v2[category]();
		default:
			if (isPill(device.type)) {
				addOns = format_gen2_addons_for_scenes(getAddonStatus(device));
				return _init_device_actions_v2['addons'](device.type, addOns);
			}
			return arr;
	}
};

/**
 * Restores the selected device and Change state components when editing.
 * DeviceStep normally does this, but edit mode can jump straight to Change
 * state, so that step never mounts. Safe to call more than once.
 */
export function bootstrapEditedDeviceAction(
	sceneData: any,
	devices: Record<string, Device>,
): { missingDevice?: { title: string; value: string | undefined; id: string } } {
	if (!is_device_action_ui(sceneData.translated)) {
		return {};
	}

	if (sceneData.selectedDevice?.id || sceneData.selectedDevice?.value) {
		return {};
	}

	const channel = Number(sceneData.translated.device.channel);
	const deviceId =
		channel > 0
			? `${sceneData.translated.device.id}_${sceneData.translated.device.channel}`
			: sceneData.translated.device.id;

	sceneData.selectedDevice = {
		value: deviceId,
		title: devices[deviceId]?.name || 'Missing or deleted device',
		id: deviceId,
	};

	sceneData.deviceAction.actions = [...sceneData.translated.actions];

	const device = devices[sceneData.selectedDevice.value];
	let missingDevice: { title: string; value: string | undefined; id: string } | undefined;

	if (device) {
		sceneData.deviceAction.components = _init_action_components_v2(
			devices[device.id],
			readDeviceStatus(real_id(device.id)),
			readDeviceSettings(real_id(device.id)),
		);
	} else {
		missingDevice = {
			title: deviceId,
			value: deviceId || undefined,
			id: deviceId,
		};
		sceneData.deviceAction.components = sceneData.deviceAction.components || [];
	}

	if (sceneData.translated.delay !== undefined) {
		sceneData.deviceAction.delay = { ...sceneData.translated.delay };
	}
	if (sceneData.translated.timer !== undefined) {
		sceneData.deviceAction.timer = { ...sceneData.translated.timer };
	}

	return missingDevice ? { missingDevice } : {};
}
